import { useInfiniteQuery } from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'hooks/api/api';
import {
  TCAction,
  TCActionListResponse,
  AutomationsActionsService,
} from 'src/api/generated';

interface ActionsResponse extends ApiResponse {
  actions: TCAction[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Hook to fetch automation actions (catalog).
 *
 * Note: the integrations catalog currently returns `nextPageToken: null` and
 * does not support `limit/pageToken` params yet; this hook fetches the full
 * list and applies an optional local `limit` slice.
 *
 * @param limit Optional number of actions to fetch per page
 * @param enabled Whether the query should be enabled, defaults to true
 * @returns Paginated list of actions and status information
 */
export const useActions = (
  limit?: number,
  enabled = true,
  includeHidden = false
): ActionsResponse => {
  const fetchActions = async ({
    pageParam: _pageParam,
  }: {
    pageParam: unknown;
  }): Promise<TCActionListResponse> => {
    // The integrations actions catalog endpoint does not support pagination yet
    // (it returns `nextPageToken: null`). Fetch the full catalog and apply a
    // local `limit` slice when requested.
    return await AutomationsActionsService.listActions(
      undefined,
      undefined,
      includeHidden
    );
  };

  const getNextPageParam = (lastPage: TCActionListResponse) =>
    lastPage.nextPageToken ?? undefined;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['automations/actions', includeHidden],
    queryFn: fetchActions,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const allActions = data?.pages.flatMap((page) => page.items) || [];
  const actions = useMemoizedArrayData(
    typeof limit === 'number' ? allActions.slice(0, limit) : allActions
  );

  return {
    loading,
    error,
    actions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};
